import {
  HOVER_IDLE_SAVE_DATA_VERSION,
  LEGACY_HOVER_IDLE_SAVE_DATA_VERSION,
  PREVIOUS_HOVER_IDLE_SAVE_DATA_VERSION,
  type HoverIdleSaveData,
} from "./hoverIdleSaveData";
import {
  createDefaultOfflineRewardSaveMetadata,
  type OfflineRewardSaveMetadata,
} from "./offlineRewardSaveMetadata";
import {
  OFFLINE_REWARD_RECEIPT_VERSION,
  type OfflineRewardReceipt,
} from "@/features/hoverIdle/offline/offlineRewardReceipt";
import { normalizeAchievementsState } from "@/features/achievements/achievementIdAliases";

export type HoverIdleSaveMigrationResult =
  | {
      readonly ok: true;
      readonly data: HoverIdleSaveData;
    }
  | {
      readonly ok: false;
      readonly reason: string;
    };

export function migrateHoverIdleSaveData(
  value: unknown,
): HoverIdleSaveMigrationResult {
  if (!isRecord(value)) {
    return fail("save data must be an object");
  }

  if (value.version === LEGACY_HOVER_IDLE_SAVE_DATA_VERSION) {
    const baselineAt = readLegacyBaseline(value.timestamps);
    return {
      ok: true,
      data: {
        ...value,
        version: HOVER_IDLE_SAVE_DATA_VERSION,
        achievements: migrateLegacyAchievements(value.achievements),
        timestamps: migrateLegacyTimestamps(value.timestamps),
        offlineReward: createDefaultOfflineRewardSaveMetadata(baselineAt),
      } as unknown as HoverIdleSaveData,
    };
  }

  if (value.version === PREVIOUS_HOVER_IDLE_SAVE_DATA_VERSION) {
    const baselineAt = readLegacyBaseline(value.timestamps);
    const { settlement, ...saveWithoutLegacyMetadata } = value;
    return {
      ok: true,
      data: {
        ...saveWithoutLegacyMetadata,
        version: HOVER_IDLE_SAVE_DATA_VERSION,
        achievements: migrateLegacyAchievements(value.achievements),
        timestamps: migrateLegacyTimestamps(value.timestamps),
        offlineReward: migrateLegacySettlementMetadata(settlement, baselineAt),
      } as unknown as HoverIdleSaveData,
    };
  }

  if (value.version !== HOVER_IDLE_SAVE_DATA_VERSION) {
    return fail(`unsupported save data version: ${String(value.version)}`);
  }

  return {
    ok: true,
    data: value as unknown as HoverIdleSaveData,
  };
}

function migrateLegacyAchievements(value: unknown): unknown {
  if (
    !isRecord(value) ||
    !Array.isArray(value.unlockedIds) ||
    !value.unlockedIds.every((id) => typeof id === "string") ||
    !Array.isArray(value.seenIds) ||
    !value.seenIds.every((id) => typeof id === "string")
  ) {
    return value;
  }
  return normalizeAchievementsState({
    unlockedIds: value.unlockedIds,
    seenIds: value.seenIds,
  });
}

function migrateLegacySettlementMetadata(
  value: unknown,
  baselineAt: number,
): OfflineRewardSaveMetadata {
  if (!isRecord(value)) {
    return createDefaultOfflineRewardSaveMetadata(baselineAt);
  }
  const revision =
    Number.isSafeInteger(value.revision) && (value.revision as number) >= 0
      ? (value.revision as number)
      : 0;
  const lastReceipt = migrateLegacyTimOnlyReceipt(value.lastReceipt);
  if (lastReceipt === null) {
    return {
      revision,
      baselineAt,
      lastReceipt: null,
      appliedRewardIds: [],
    };
  }
  const legacyIds = Array.isArray(value.appliedSettlementIds)
    ? value.appliedSettlementIds
    : [];
  return {
    revision,
    baselineAt: lastReceipt.baseline.committedAt,
    lastReceipt,
    appliedRewardIds: legacyIds.includes(lastReceipt.rewardId)
      ? [lastReceipt.rewardId]
      : [],
  };
}

function readLegacyBaseline(value: unknown): number {
  if (!isRecord(value)) return 0;
  if (isFiniteNonNegativeNumber(value.offlineBaselineAt)) {
    return value.offlineBaselineAt;
  }
  return isFiniteNonNegativeNumber(value.savedAt) ? value.savedAt : 0;
}

function migrateLegacyTimestamps(value: unknown): unknown {
  if (!isRecord(value)) return value;
  const { offlineBaselineAt: _offlineBaselineAt, ...timestamps } = value;
  return timestamps;
}

function migrateLegacyTimOnlyReceipt(
  value: unknown,
): OfflineRewardReceipt | null {
  if (!isRecord(value) || !isRecord(value.offlineReward)) {
    return null;
  }
  const reward = value.offlineReward;
  if (reward.kind !== "tim-only") return null;
  const rewardId =
    typeof reward.offlineRewardId === "string"
      ? reward.offlineRewardId
      : typeof value.settlementId === "string"
        ? value.settlementId
        : null;
  if (
    rewardId === null ||
    typeof reward.timGained !== "string" ||
    typeof value.resultDigest !== "string" ||
    typeof reward.gameElapsedMs !== "number" ||
    !Number.isFinite(reward.gameElapsedMs) ||
    reward.gameElapsedMs < 0 ||
    typeof reward.wasCapped !== "boolean" ||
    !Number.isSafeInteger(reward.commitRevision) ||
    (reward.commitRevision as number) < 0 ||
    typeof reward.baselineAt !== "number" ||
    !Number.isFinite(reward.baselineAt) ||
    reward.baselineAt < 0
  ) {
    return null;
  }
  const previousAt =
    typeof value.startBaselineAt === "number" &&
    Number.isFinite(value.startBaselineAt) &&
    value.startBaselineAt >= 0
      ? value.startBaselineAt
      : reward.baselineAt;
  return {
    version: OFFLINE_REWARD_RECEIPT_VERSION,
    rewardId,
    gameElapsedMs: reward.gameElapsedMs,
    wasCapped: reward.wasCapped,
    timGained: reward.timGained,
    resultDigest: value.resultDigest,
    commitRevision: reward.commitRevision as number,
    baseline: {
      previousAt,
      committedAt: reward.baselineAt,
    },
  };
}

function fail(reason: string): HoverIdleSaveMigrationResult {
  return { ok: false, reason };
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNonNegativeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
